/* LumetierPreview — the brand book at /lumetier-preview.
   Phase 1 revamp: cream paper canvas with subtle texture; 3-column body
   (lockups & palette / type specimens / icon set); IssueSeal in the top-right;
   serif italic closing line. Built entirely from Phase 0 primitives. */

import React from "react";
import { Link } from "react-router-dom";
import {
  ChapterEyebrow, DisplayHeadline, GoldRule, GoldFoilButton, HairlineButton,
  FilmGrain, IssueSeal,
} from "@/components/editorial";
import useEditorialReveal from "@/hooks/useEditorialReveal";

const SWATCHES = [
  { hex: "#C9A961", name: "Champagne Gold", token: "--gold-base" },
  { hex: "#D4B872", name: "Gold Foil",      token: "--gold-foil" },
  { hex: "#A88940", name: "Gold Deep",      token: "--gold-deep" },
  { hex: "#FAF9F6", name: "Cream",          token: "--cream",          border: true },
  { hex: "#F5F2EC", name: "Cream Paper",    token: "--cream-paper",    border: true },
  { hex: "#D4C5B9", name: "Soft Taupe",     token: "--accent" },
  { hex: "#1A1816", name: "Charcoal Soft",  token: "--charcoal-soft" },
  { hex: "#0E0D0C", name: "Charcoal",       token: "--charcoal" },
];

export default function LumetierPreview() {
  useEditorialReveal();
  return (
    <div className="min-h-screen bg-[color:var(--cream-paper)] text-[color:var(--charcoal)]" data-testid="brand-book">
      {/* Header banner */}
      <header className="relative border-b border-[color:var(--charcoal)]/12">
        <FilmGrain opacity={0.05} tone="warm" />
        <div className="relative max-w-[1480px] mx-auto px-6 lg:px-12 py-12 lg:py-16 grid md:grid-cols-[auto_1fr_auto] items-center gap-8">
          <Link to="/" className="ed-eyebrow text-[color:var(--gold-deep)] hover:text-[color:var(--gold-base)] transition-colors">
            ← Back to Lumetier
          </Link>
          <div className="text-center">
            <div className="ed-eyebrow text-[color:var(--gold-deep)]">BRAND BOOK · VOL. 01 · 2026</div>
            <div className="font-serif text-5xl md:text-6xl mt-2" style={{ letterSpacing: "0.38em" }}>LUMETIER</div>
          </div>
          <div className="justify-self-end">
            <IssueSeal issue="01" season="Vol" year="2026" tone="gold" size={120} />
          </div>
        </div>
      </header>

      {/* Big editorial cover headline */}
      <section className="relative border-b border-[color:var(--charcoal)]/12">
        <FilmGrain opacity={0.03} tone="warm" />
        <div className="relative max-w-[1480px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <ChapterEyebrow chapter="I" label="The Brand" />
          <GoldRule width="3rem" className="mt-4 mb-8" />
          <DisplayHeadline size="cover" data-reveal="rise">
            One quiet language for the <em>whole</em> studio.
          </DisplayHeadline>
          <p className="ed-body-lg mt-8 max-w-2xl text-[color:var(--charcoal)]/70">
            Lumetier's design system is the spine that holds every surface — hub, products, galleries, admin — together. This book is the standard.
          </p>
        </div>
      </section>

      {/* 3-column body */}
      <section className="relative">
        <FilmGrain opacity={0.03} tone="warm" />
        <div className="relative max-w-[1480px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-3 gap-10 lg:gap-14">
          {/* LEFT — lockups + palette */}
          <div data-reveal="rise">
            <ChapterEyebrow chapter="I" label="Lockups & Palette" />
            <GoldRule width="2rem" className="mt-3 mb-7" />

            <div className="space-y-7">
              <div className="p-7 bg-[color:var(--cream)] border border-[color:var(--charcoal)]/12">
                <div className="ed-caption uppercase tracking-[0.22em] text-[color:var(--gold-deep)] mb-4">Wordmark</div>
                <div className="font-serif text-3xl" style={{ letterSpacing: "0.38em" }}>LUMETIER</div>
              </div>
              <div className="p-7 bg-[color:var(--cream)] border border-[color:var(--charcoal)]/12">
                <div className="ed-caption uppercase tracking-[0.22em] text-[color:var(--gold-deep)] mb-4">Monogram & Lockup</div>
                <div className="flex items-center gap-4">
                  <img src="/lumetier-icon.svg" alt="Lumetier monogram" className="w-12 h-12" />
                  <div className="font-serif text-2xl" style={{ letterSpacing: "0.28em" }}>LUMETIER</div>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <div className="ed-eyebrow text-[color:var(--gold-deep)] mb-5">Palette</div>
              <div className="grid grid-cols-2 gap-3">
                {SWATCHES.map((s) => (
                  <div key={s.hex} className="flex items-center gap-3">
                    <div
                      className={`w-12 h-12 shrink-0 ${s.border ? "border border-[color:var(--charcoal)]/15" : ""}`}
                      style={{ background: s.hex }}
                    />
                    <div className="min-w-0">
                      <div className="font-serif text-[15px] leading-tight">{s.name}</div>
                      <div className="ed-caption uppercase tracking-[0.18em] text-[color:var(--charcoal)]/55 font-mono">{s.hex}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* MIDDLE — type specimens */}
          <div data-reveal="rise">
            <ChapterEyebrow chapter="II" label="Typography" />
            <GoldRule width="2rem" className="mt-3 mb-7" />

            <div className="p-7 bg-[color:var(--cream)] border border-[color:var(--charcoal)]/12 mb-5 relative">
              <div className="ed-caption uppercase tracking-[0.22em] text-[color:var(--gold-deep)] mb-3">Display · Serif</div>
              <div className="font-serif text-[200px] leading-[0.85] text-[color:var(--charcoal)] tracking-tight">Aa</div>
              <div className="mt-4 font-serif italic text-3xl text-[color:var(--gold-base)]">craft</div>
              <div className="ed-caption mt-5 uppercase tracking-[0.22em] text-[color:var(--charcoal)]/55">Cormorant Garamond — 400 / 500 / 600 / Italic</div>
            </div>

            <div className="p-7 bg-[color:var(--cream)] border border-[color:var(--charcoal)]/12">
              <div className="ed-caption uppercase tracking-[0.22em] text-[color:var(--gold-deep)] mb-3">Body · Sans</div>
              <div className="text-[180px] leading-[0.85] text-[color:var(--charcoal)] tracking-tight" style={{fontFamily: "'Manrope', system-ui, sans-serif", fontWeight: 300}}>Aa</div>
              <div className="ed-caption mt-5 uppercase tracking-[0.22em] text-[color:var(--charcoal)]/55">Manrope — 200 / 300 / 400 / 500 / 600 / 700</div>
            </div>
          </div>

          {/* RIGHT — icon set */}
          <div data-reveal="rise">
            <ChapterEyebrow chapter="III" label="Icon Set" />
            <GoldRule width="2rem" className="mt-3 mb-7" />

            <div className="grid grid-cols-2 gap-3">
              {[
                { src: "/lumetier-icon.svg",     name: "SVG · vector" },
                { src: "/lumetier-icon-32.png",  name: "32 · favicon" },
                { src: "/lumetier-icon-192.png", name: "192 · PWA" },
                { src: "/lumetier-icon-512.png", name: "512 · PWA" },
                { src: "/lumetier-apple-touch.png", name: "Apple touch · 180" },
              ].map((ic) => (
                <div key={ic.name} className="aspect-square bg-[color:var(--cream)] border border-[color:var(--charcoal)]/12 flex flex-col items-center justify-center p-4">
                  <img src={ic.src} alt={ic.name} className="w-14 h-14 object-contain" />
                  <div className="ed-caption mt-3 uppercase tracking-[0.18em] text-[color:var(--charcoal)]/55 text-center">{ic.name}</div>
                </div>
              ))}
              <div className="aspect-square bg-[color:var(--charcoal)] text-[color:var(--cream)] flex flex-col items-center justify-center p-4 border border-[color:var(--charcoal)]">
                <img src="/lumetier-icon.svg" alt="On dark" className="w-14 h-14 object-contain" />
                <div className="ed-caption mt-3 uppercase tracking-[0.18em] text-[color:var(--cream)]/60 text-center">On charcoal</div>
              </div>
            </div>

            <div className="mt-9 p-7 border border-[color:var(--charcoal)]/12 bg-[color:var(--cream)]">
              <div className="ed-caption uppercase tracking-[0.22em] text-[color:var(--gold-deep)] mb-3">CTAs</div>
              <div className="space-y-3">
                <GoldFoilButton href="#" size="sm">Open your studio</GoldFoilButton>
                <HairlineButton href="#" size="sm" arrow={false}>See the long version</HairlineButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Closing line */}
      <section className="relative border-t border-[color:var(--charcoal)]/12 py-20 text-center">
        <FilmGrain opacity={0.03} tone="warm" />
        <div className="relative max-w-3xl mx-auto px-6">
          <GoldRule width="4rem" align="center" />
          <p className="font-serif italic text-2xl md:text-3xl mt-6 text-[color:var(--charcoal)]">
            Lumetier — the studio platform for modern <em className="text-[color:var(--gold-base)]">photographers</em>.
          </p>
          <p className="ed-eyebrow text-[color:var(--gold-deep)] mt-8">END OF VOLUME 01</p>
        </div>
      </section>
    </div>
  );
}
